import React, { useState } from 'react';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Card, CardContent } from './ui/card';
import {
    Calendar,
    MapPin,
    Clock,
    Users,
    Send,
    Loader2
} from 'lucide-react';
import { ActivityWithApplication } from '../types/activity';


interface ApplicationModalProps {
    isOpen: boolean;
    onClose: () => void;
    activity: ActivityWithApplication;
    onSubmit: (activityId: string, note: string) => Promise<void>;
}

export function ApplicationModal({
    isOpen,
    onClose,
    activity,
    onSubmit
}: ApplicationModalProps) {
    const [note, setNote] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const date = new Date(activity.dateTime).toLocaleDateString();
    const time = new Date(activity.dateTime).toLocaleTimeString();

    const handleSubmit = async () => {
        if (!activity) return;

        setIsSubmitting(true);
        try {
            await onSubmit(activity.id, note);
            setNote(''); // Clear the note after successful submission
            onClose();
        } catch (error) {
            console.error('Failed to submit application:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        if (!isSubmitting) {
            setNote(''); // Clear note when closing without submitting
            onClose();
        }
    };

    if (!activity) return null;

    return (
        <Dialog open={isOpen} onOpenChange={handleClose}>
            <DialogContent className="sm:max-w-lg">
                <DialogHeader>
                    <DialogTitle>Apply to Join Activity</DialogTitle>
                    <DialogDescription>
                        Send your application to join this activity. You can include an optional message to the organizer.
                    </DialogDescription>
                </DialogHeader>

                {/* Activity Summary */}
                <Card className="border-muted">
                    <CardContent className="p-4">
                        <h3 className="font-semibold mb-2">{activity.title}</h3>
                        <div className="grid grid-cols-1 gap-2 text-sm text-muted-foreground">
                            <div className="flex items-center space-x-2">
                                <Calendar size={14} />
                                <span>{date}</span>
                            </div>
                            <div className="flex items-center space-x-2">
                                <Clock size={14} />
                                <span>{time}</span>
                            </div>
                            <div className="flex items-center space-x-2">
                                <MapPin size={14} />
                                <span>{activity.location?.name.split(',')[0]}</span>
                            </div>
                            <div className="flex items-center space-x-2">
                                <Users size={14} />
                                <span>{activity.participants}/{activity.maxParticipants} participants</span>
                            </div>
                        </div>
                        <div className="mt-2 pt-2 border-t border-muted">
                            <p className="text-sm">
                                <span className="font-medium">Organizer:</span> {activity.creator?.name}
                            </p>
                        </div>
                    </CardContent>
                </Card>

                {/* Note Section */}
                <div className="space-y-2">
                    <Label htmlFor="application-note">
                        Message to Organizer (Optional)
                    </Label>
                    <Textarea
                        id="application-note"
                        placeholder="Tell the organizer why you'd like to join this activity or any relevant information..."
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        rows={4}
                        className="resize-none"
                        disabled={isSubmitting}
                    />
                    <p className="text-xs text-muted-foreground">
                        This message will be visible to the organizer when they review your application.
                    </p>
                </div>

                <DialogFooter className="flex flex-col sm:flex-row gap-2">
                    <Button
                        variant="outline"
                        onClick={handleClose}
                        disabled={isSubmitting}
                        className="w-full sm:w-auto"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                        className="w-full sm:w-auto"
                    >
                        {isSubmitting ? (
                            <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                Submitting...
                            </>
                        ) : (
                            <>
                                <Send className="w-4 h-4 mr-2" />
                                Submit Application
                            </>
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}